import CiviCatalogTools from '@/components/catalog/CiviCatalogTools'
import ModelReconciliationTools from '@/components/catalog/ModelReconciliationTools'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  useCatalogCategories,
  useCatalogClasses,
  useCatalogClass,
  useCreateCatalogClass,
  useUpdateCatalogClass,
  useCurrentUser,
  useCreateCatalogSubtype,
  useUpdateCatalogSubtype,
} from '@/api/hooks'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { getCategoryBadgeClass } from '@/lib/classColors'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import RulesViewer from '@/components/catalog/RulesViewer'
import { normalizeAliases, safeJsonParse } from '@/lib/catalog'
import { parseLabelsPerExhibitConfig } from '@/lib/labels-per-exhibit'
import { ApiError, type CatalogClassOutput } from '@schemas'

const EMPTY_RULES_TEMPLATE = `{
  "container": {
    "type": "",
    "material": "",
    "notes": ""
  },
  "quantity": {},
  "labels": {},
  "judgingCriteria": []
}`

type CatalogClassFormState = {
  categoryCode: string
  name: string
  description: string
  aliases: string
  flags: string
  rules: string
}

const defaultFormState: CatalogClassFormState = {
  categoryCode: '',
  name: '',
  description: '',
  aliases: '',
  flags: '',
  rules: EMPTY_RULES_TEMPLATE,
}

function formatCategoryLabel(code: string, name?: string) {
  if (!name) return code
  return `${name}`
}

function formatDate(iso?: string) {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso))
  } catch (_error) {
    return iso
  }
}

function normalizeFlagKey(flag: string) {
  return flag.toLowerCase()
}

function formatFlagLabel(flag: string) {
  return flag
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, char => char.toUpperCase())
    .replace(/\s+(\w)/g, (_, char: string) => ` ${char.toUpperCase()}`)
}

function getFlagKeys(flags: unknown): string[] {
  if (!flags) return []
  if (typeof flags === 'string') return [normalizeFlagKey(flags)]
  if (typeof flags === 'object' && !Array.isArray(flags)) {
    return Object.entries(flags as Record<string, unknown>)
      .filter(([, value]) => Boolean(value))
      .map(([key]) => normalizeFlagKey(key))
  }
  return []
}

export default function ModelClassesPage() {
  const isAdmin = useCurrentUser().data?.isAdmin === true
  const [includeRetired, setIncludeRetired] = useState(false)
  const [editingModel, setEditingModel] = useState<CatalogClassOutput['catalogClass'] | null>(null)
  const [conflict, setConflict] = useState(false)
  const [subtypeDraft, setSubtypeDraft] = useState({ id: '', name: '', description: '' })
  const createSubtype = useCreateCatalogSubtype()
  const updateSubtype = useUpdateCatalogSubtype()
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>()
  const [selectedFlag, setSelectedFlag] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const [sheetOpen, setSheetOpen] = useState(false)
  const [isCreateMode, setIsCreateMode] = useState(false)
  const [activeClassId, setActiveClassId] = useState<string | null>(null)
  const [formState, setFormState] = useState<CatalogClassFormState>(defaultFormState)
  const [formError, setFormError] = useState<string | null>(null)

  const categoriesQuery = useCatalogCategories()
  const allClassesQuery = useCatalogClasses({ includeRetired: true })
  const searchQuery = search.trim() || undefined
  const classesQuery = useCatalogClasses({
    category: selectedCategory,
    q: searchQuery,
    flag: selectedFlag,
    includeRetired,
  })
  const selectedClassQuery = useCatalogClass(activeClassId ?? undefined)
  const createMutation = useCreateCatalogClass()
  const updateMutation = useUpdateCatalogClass()

  const classes = classesQuery.data ?? []
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data])
  const flagOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const catalogClass of allClassesQuery.data ?? []) {
      for (const flagKey of getFlagKeys(catalogClass.flags)) {
        if (!map.has(flagKey)) {
          map.set(flagKey, formatFlagLabel(flagKey))
        }
      }
    }
    return Array.from(map.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [allClassesQuery.data])

  useEffect(() => {
    if (!sheetOpen) {
      setFormError(null)
    }
  }, [sheetOpen])

  useEffect(() => {
    if (!sheetOpen || !selectedClassQuery.data || isCreateMode || editingModel) return
    const catalogClass = selectedClassQuery.data
    setEditingModel(catalogClass)
    const flagString = catalogClass.flags ? JSON.stringify(catalogClass.flags, null, 2) : ''
    const aliasesString = Array.isArray(catalogClass.aliases) ? catalogClass.aliases.join(', ') : ''
    const rulesString = catalogClass.rules
      ? JSON.stringify(catalogClass.rules, null, 2)
      : EMPTY_RULES_TEMPLATE

    setFormState({
      categoryCode: catalogClass.categoryCode,
      name: catalogClass.name,
      description: catalogClass.description ?? '',
      aliases: aliasesString,
      flags: flagString,
      rules: rulesString,
    })
  }, [sheetOpen, selectedClassQuery.data, isCreateMode, editingModel])

  useEffect(() => {
    if (selectedFlag && flagOptions.every(option => option.key !== selectedFlag)) {
      setSelectedFlag(undefined)
    }
  }, [selectedFlag, flagOptions])

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    createSubtype.isPending ||
    updateSubtype.isPending

  const handleOpenCreate = () => {
    setEditingModel(null)
    setConflict(false)
    setSubtypeDraft({ id: '', name: '', description: '' })
    setIsCreateMode(true)
    setActiveClassId(null)
    setFormState(prev => ({
      ...defaultFormState,
      categoryCode: selectedCategory ?? prev.categoryCode,
    }))
    setSheetOpen(true)
  }

  const handleRowClick = (catalogClass: CatalogClassOutput['catalogClass']) => {
    setEditingModel(null)
    setConflict(false)
    setSubtypeDraft({ id: '', name: '', description: '' })
    setIsCreateMode(false)
    setActiveClassId(catalogClass.id)
    setSheetOpen(true)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!isAdmin || conflict || isSaving) return
    setFormError(null)

    if (!formState.categoryCode) {
      setFormError('Select a category before saving.')
      return
    }

    if (!formState.name.trim()) {
      setFormError('Name is required.')
      return
    }

    let parsedRules: unknown
    try {
      parsedRules = JSON.parse(formState.rules)
    } catch (_error) {
      setFormError('Rules must be valid JSON.')
      return
    }

    if (!parsedRules || typeof parsedRules !== 'object' || Array.isArray(parsedRules)) {
      setFormError('Rules must be a JSON object.')
      return
    }

    let flagsPayload: Record<string, unknown> | string | undefined
    if (formState.flags.trim()) {
      const parsed = safeJsonParse<unknown>(formState.flags, null)
      if (parsed === null) {
        setFormError('Flags must be valid JSON or left blank.')
        return
      }
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setFormError('Flags JSON must be an object.')
        return
      }
      flagsPayload = parsed as Record<string, unknown>
    }

    const payload = {
      categoryCode: formState.categoryCode,
      name: formState.name.trim(),
      description: formState.description.trim() || undefined,
      aliases: normalizeAliases(formState.aliases),
      flags: flagsPayload,
      rules: parsedRules as Record<string, unknown>,
    }

    try {
      if (isCreateMode) {
        await createMutation.mutateAsync(payload)
        toast.success('Catalog class created')
      } else if (activeClassId && editingModel) {
        await updateMutation.mutateAsync({
          catalogClassId: activeClassId,
          payload: {
            ...payload,
            description: payload.description ?? null,
            aliases: payload.aliases ?? null,
            flags: payload.flags ?? null,
            expectedRevision: editingModel.revision,
          },
        })
        toast.success('Catalog class updated')
      }
      setSheetOpen(false)
    } catch (error) {
      handleMutationError(error)
    }
  }

  function handleMutationError(error: unknown) {
    if (error instanceof ApiError && error.status === 409) setConflict(true)
    setFormError(error instanceof Error ? error.message : 'Unable to save catalog changes')
  }

  async function changeLifecycle() {
    if (!editingModel || !isAdmin || conflict || isSaving) return
    try {
      const model = await updateMutation.mutateAsync({
        catalogClassId: editingModel.id,
        payload: { expectedRevision: editingModel.revision, retired: !editingModel.retired },
      })
      setEditingModel(model)
      setFormError(null)
    } catch (error) {
      handleMutationError(error)
    }
  }

  async function saveSubtype(retired?: boolean, subtypeId = subtypeDraft.id) {
    if (!editingModel || !isAdmin || conflict || isSaving) return
    try {
      const existing = editingModel.subtypes.find(item => item.id === subtypeId)
      const model = existing
        ? await updateSubtype.mutateAsync({
            catalogClassId: editingModel.id,
            subtypeId: existing.id,
            payload: {
              expectedRevision: existing.revision,
              expectedModelRevision: editingModel.revision,
              ...(retired === undefined
                ? { name: subtypeDraft.name, description: subtypeDraft.description || null }
                : { retired }),
            },
          })
        : await createSubtype.mutateAsync({
            catalogClassId: editingModel.id,
            payload: {
              expectedRevision: editingModel.revision,
              name: subtypeDraft.name,
              description: subtypeDraft.description || null,
            },
          })
      setEditingModel(model)
      if (retired === undefined) setSubtypeDraft({ id: '', name: '', description: '' })
      setFormError(null)
    } catch (error) {
      handleMutationError(error)
    }
  }

  async function reloadModel() {
    const result = await selectedClassQuery.refetch()
    if (!result.data || result.error) return
    setEditingModel(null)
    setSubtypeDraft({ id: '', name: '', description: '' })
    setConflict(false)
    setFormError(null)
  }

  const handleSheetOpenChange = (open: boolean) => {
    setSheetOpen(open)
    if (!open) {
      setActiveClassId(null)
      setIsCreateMode(false)
      setFormState(defaultFormState)
      setFormError(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Model Classes</h1>
        <p className="text-muted-foreground">
          Browse reusable model classes and stable subtypes. Administrators manage their defaults
          and lifecycle.
        </p>
      </div>

      {isAdmin && <CiviCatalogTools models={allClassesQuery.data ?? []} categories={categories} />}
      {isAdmin && <ModelReconciliationTools />}

      <div className="grid gap-6 lg:grid-cols-[280px,1fr]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Search</label>
              <Input
                placeholder="Search by name or description"
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-muted-foreground">Category</label>
                {selectedCategory && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCategory(undefined)}>
                    Clear
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="catalog-category-list">
                {categories.map(category => (
                  <Button
                    key={category.code}
                    type="button"
                    variant={selectedCategory === category.code ? 'default' : 'outline'}
                    size="sm"
                    data-testid={`catalog-category-${category.code}`}
                    onClick={() => setSelectedCategory(category.code)}
                  >
                    {formatCategoryLabel(category.code, category.name)}
                  </Button>
                ))}
              </div>
            </div>

            {flagOptions.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-muted-foreground">Flags</label>
                  {selectedFlag && (
                    <Button variant="ghost" size="sm" onClick={() => setSelectedFlag(undefined)}>
                      Clear
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="catalog-flag-list">
                  {flagOptions.map(option => (
                    <Button
                      key={option.key}
                      type="button"
                      variant={selectedFlag === option.key ? 'default' : 'outline'}
                      size="sm"
                      data-testid={`catalog-flag-${option.key}`}
                      onClick={() =>
                        setSelectedFlag(prev => (prev === option.key ? undefined : option.key))
                      }
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeRetired}
                onChange={event => setIncludeRetired(event.target.checked)}
              />
              Include retired models
            </label>
            {isAdmin && (
              <Button className="w-full" onClick={handleOpenCreate}>
                New Catalog Class
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex items-center justify-between py-4">
            <CardTitle className="text-lg">Catalog Classes</CardTitle>
            <div className="text-sm text-muted-foreground">
              {classesQuery.isFetching ? 'Loading…' : `${classes.length} classes`}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Labels required</TableHead>
                    <TableHead className="w-[160px]">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classes.map(catalogClass => (
                    <TableRow
                      key={catalogClass.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => handleRowClick(catalogClass)}
                      data-testid={`catalog-class-row-${catalogClass.id}`}
                    >
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Badge
                            variant="secondary"
                            className={getCategoryBadgeClass(catalogClass.categoryCode)}
                          >
                            {catalogClass.categoryCode}
                          </Badge>
                          {getFlagKeys(catalogClass.flags).map(flagKey => (
                            <Badge key={`${catalogClass.id}-flag-${flagKey}`} variant="outline">
                              {formatFlagLabel(flagKey)}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {catalogClass.name}{' '}
                        {catalogClass.retired && <Badge variant="outline">Retired</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          ×{parseLabelsPerExhibitConfig(catalogClass.rules) ?? 1} labels/exhibit
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(catalogClass.updatedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!classesQuery.isFetching && classes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        No catalog classes found. Try adjusting the filters or create a new class.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Sheet open={sheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent className="w-full sm:max-w-xl">
          <form onSubmit={handleSubmit} className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle>
                {isCreateMode ? 'Create Catalog Class' : 'Catalog Class Details'}
              </SheetTitle>
              <SheetDescription>
                {isCreateMode
                  ? 'Define a reusable catalog class that organizers can add to shows.'
                  : 'Review and update catalog class metadata and default rules.'}
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 space-y-6 overflow-y-auto px-4">
              <fieldset
                disabled={!isAdmin || isSaving || conflict || (!isCreateMode && !editingModel)}
                className="grid gap-4"
              >
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Category Code</label>
                  <Input
                    value={formState.categoryCode}
                    onChange={event =>
                      setFormState(prev => ({
                        ...prev,
                        categoryCode: event.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="H"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    value={formState.name}
                    onChange={event =>
                      setFormState(prev => ({ ...prev, name: event.target.value }))
                    }
                    placeholder="Light Extracted Honey"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Description</label>
                  <Input
                    value={formState.description}
                    onChange={event =>
                      setFormState(prev => ({ ...prev, description: event.target.value }))
                    }
                    placeholder="Optional context for organizers"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Aliases (comma separated)</label>
                  <Input
                    value={formState.aliases}
                    onChange={event =>
                      setFormState(prev => ({ ...prev, aliases: event.target.value }))
                    }
                    placeholder="Alternative names"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Flags JSON (optional)</label>
                  <textarea
                    value={formState.flags}
                    onChange={event =>
                      setFormState(prev => ({ ...prev, flags: event.target.value }))
                    }
                    className="min-h-[100px] rounded-md border bg-background px-3 py-2 text-sm font-mono"
                    placeholder='e.g. {"novice": true}'
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Rules JSON</label>
                  <textarea
                    value={formState.rules}
                    onChange={event =>
                      setFormState(prev => ({ ...prev, rules: event.target.value }))
                    }
                    className="min-h-[200px] rounded-md border bg-background px-3 py-2 text-sm font-mono"
                    placeholder={EMPTY_RULES_TEMPLATE}
                  />
                </div>
              </fieldset>

              {!isCreateMode && selectedClassQuery.data && (
                <div className="grid gap-4 rounded-lg border p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Current Rules Preview
                  </h3>
                  <RulesViewer rules={selectedClassQuery.data.rules} className="text-sm" />
                </div>
              )}

              {editingModel && !isCreateMode && (
                <section
                  className="space-y-4 rounded-lg border p-4"
                  aria-label="Model lifecycle and subtypes"
                >
                  <p>
                    {editingModel.retired ? 'Retired' : 'Active'} · Revision {editingModel.revision}
                  </p>
                  <p>
                    Source:{' '}
                    {editingModel.origin === 'legacy'
                      ? 'Unverified legacy catalog'
                      : editingModel.origin === 'app'
                        ? 'App-created'
                        : editingModel.origin === 'authored'
                          ? 'Authored package'
                          : 'CiviCRM'}
                  </p>
                  {editingModel.origin === 'wpcivi' && (
                    <dl className="text-sm break-all">
                      <dt>Source instance / key</dt>
                      <dd>
                        {editingModel.sourceInstanceId} / {editingModel.sourceKey}
                      </dd>
                      <dt>Imported revision</dt>
                      <dd>{editingModel.importedDigest}</dd>
                      <dt>Publication version</dt>
                      <dd>{editingModel.publicationVersion}</dd>
                    </dl>
                  )}
                  {isAdmin && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isSaving || conflict}
                      onClick={() => void changeLifecycle()}
                    >
                      {editingModel.retired ? 'Restore model' : 'Retire model'}
                    </Button>
                  )}
                  <h3 className="font-semibold">Subtypes</h3>
                  <p className="text-sm text-muted-foreground">
                    Renames preserve identity. Create a new subtype for a different variety. Retired
                    models and subtypes remain available in history.
                  </p>
                  <ul className="space-y-3">
                    {editingModel.subtypes.map(subtype => (
                      <li key={subtype.id} className="space-y-1">
                        <p>
                          {subtype.name}{' '}
                          {subtype.retired && <Badge variant="outline">Retired</Badge>}
                        </p>
                        <p className="text-sm text-muted-foreground">{subtype.description}</p>
                        <p className="text-xs break-all">
                          {subtype.id} · Revision {subtype.revision} · {subtype.origin}
                        </p>
                        {subtype.origin === 'wpcivi' && (
                          <p className="text-xs break-all">
                            {subtype.sourceInstanceId} / {subtype.sourceKey} ·{' '}
                            {subtype.publicationVersion} · {subtype.importedDigest}
                          </p>
                        )}
                        {isAdmin && (
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isSaving || conflict}
                              onClick={() =>
                                setSubtypeDraft({
                                  id: subtype.id,
                                  name: subtype.name,
                                  description: subtype.description ?? '',
                                })
                              }
                            >
                              Edit {subtype.name}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isSaving || conflict}
                              onClick={() => void saveSubtype(!subtype.retired, subtype.id)}
                            >
                              {subtype.retired ? 'Restore' : 'Retire'} {subtype.name}
                            </Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  {isAdmin && (
                    <fieldset
                      className="space-y-2"
                      disabled={isSaving || conflict || (editingModel.retired && !subtypeDraft.id)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') event.preventDefault()
                      }}
                    >
                      <legend>{subtypeDraft.id ? 'Edit subtype' : 'New subtype'}</legend>
                      <label htmlFor="subtype-name">Subtype name</label>
                      <Input
                        id="subtype-name"
                        value={subtypeDraft.name}
                        onChange={event =>
                          setSubtypeDraft(prev => ({ ...prev, name: event.target.value }))
                        }
                      />
                      <label htmlFor="subtype-description">Subtype description</label>
                      <Input
                        id="subtype-description"
                        value={subtypeDraft.description}
                        onChange={event =>
                          setSubtypeDraft(prev => ({ ...prev, description: event.target.value }))
                        }
                      />
                      <Button
                        type="button"
                        disabled={!subtypeDraft.name.trim()}
                        onClick={() => void saveSubtype()}
                      >
                        Save subtype
                      </Button>
                      {subtypeDraft.id && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setSubtypeDraft({ id: '', name: '', description: '' })}
                        >
                          Cancel subtype edit
                        </Button>
                      )}
                    </fieldset>
                  )}
                </section>
              )}
              {formError && (
                <p role="alert" className="text-sm text-destructive">
                  {formError}
                </p>
              )}
              {conflict && (
                <div role="alert" className="space-y-2">
                  <p>
                    The catalog changed. Your input is preserved. Reload the current model to
                    discard these edits, then review before saving again.
                  </p>
                  <Button type="button" variant="outline" onClick={() => void reloadModel()}>
                    Reload current model (discard edits)
                  </Button>
                </div>
              )}
            </div>

            <SheetFooter>
              <div className="flex flex-col gap-2">
                {isAdmin && (
                  <Button
                    type="submit"
                    disabled={isSaving || conflict || (!isCreateMode && !editingModel)}
                  >
                    {isSaving ? 'Saving…' : isCreateMode ? 'Create Catalog Class' : 'Save Changes'}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSheetOpenChange(false)}
                >
                  Cancel
                </Button>
              </div>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}
